// src/context/AuthContext.jsx
import { createContext, useState, useEffect } from "react";
import { refreshAccessToken, logout } from "../api/authService";
import { jwtDecode } from "jwt-decode";
import { getAccessLevel } from "../api/authService";
import { getCurrentUserModules } from "../api/userService";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        const decoded = jwtDecode(token);
        setRole(decoded.role);
        setIsAuthenticated(true);

        if (decoded.role !== "guest") {
          const newToken = await refreshAccessToken();
          if (newToken) {
            const refreshed = jwtDecode(newToken);
            setRole(refreshed.role);
            setIsAuthenticated(true);
            fetchCurrentUserModules();
          } else {
            logout();
            setIsAuthenticated(false);
            setRole(null);
            localStorage.removeItem('mirror');
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // Auto-refresh timer (skip for guests)
  useEffect(() => {
    const minutes = parseInt(process.env.REACT_APP_REFRESH_INTERVAL_MINUTES || "15", 10);
    const intervalMs = minutes * 60 * 1000;

    const interval = setInterval(async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      const decoded = jwtDecode(token);
      if (decoded.role === "guest") return;

      const newToken = await refreshAccessToken();
      if (!newToken) {
        logout();
        setIsAuthenticated(false);
        setRole(null);
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, []);

  // 🔹 Refresh on focus/visibility
  useEffect(() => {
    const handleFocus = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      const decoded = jwtDecode(token);
      if (decoded.role === "guest") return;

      const newToken = await refreshAccessToken();
      if (newToken) {
        const refreshed = jwtDecode(newToken);
        setRole(refreshed.role);
        setIsAuthenticated(true);
      } else {
        logout();
        setIsAuthenticated(false);
        setRole(null);
        localStorage.removeItem('mirror');
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const fetchCurrentUserModules = async () => {
    try {
      const response = await getCurrentUserModules();
      localStorage.setItem("mirror", response.data);
    } catch (error) {
      console.error("Error fetching current user modules:", error);
    }
  };

  const handleLogin = async (token, callback) => {
    const decoded = jwtDecode(token);
    setRole(decoded.role);
    setIsAuthenticated(true);
    if (decoded.role === 'user') {
      await fetchCurrentUserModules();
      if (callback) callback();
    } else {
      if (callback) callback();
    }
  };

  const handleLogout = () => {
    const token = localStorage.getItem("accessToken");
    let role = null;
    if (token) {
      try {
        role = jwtDecode(token).role;
      } catch {}
    }

    logout();
    setIsAuthenticated(false);
    setRole(null);
    localStorage.removeItem('mirror');

    if (role === "guest") {
      window.location.href = "/thank-you";
    } else {
      window.location.href = "/login";
    }
  };

  const checkPermission = async (subModuleName, minAccessLevel) => {
    try {
      const response = await getAccessLevel(subModuleName, minAccessLevel);
      if (response.access_level >= minAccessLevel) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error fetching access level:", error);
    }
    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        role,
        loading,
        handleLogin,
        handleLogout,
        checkPermission
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};